// Editor sheet for one session block (create / edit). A block is a doctor's
// whole working session (AM/PM/EVE) at one clinic: label + time span + weekly
// or monthly recurrence + the interval template that generates the bookable
// micro-slots. Saving re-materializes the child slots server-side.
//
// v1 interval editing is deliberately minimal: the template is summarized
// ("10 × 15 min · 5 new / 5 follow-up") with two steppers controlling the
// first-visit / follow-up mix of an auto-laid-out 15-minute grid. A migrated
// block's hand-tuned pattern is preserved untouched until the steppers or the
// time span are changed.

import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { DatePicker } from "@/components/ui/date-picker";
import { cn } from "@/lib/utils";
import { appTimeZone } from "@/lib/timezone";
import { fetchTenants } from "@/lib/api/tenants";
import { fetchDoctors } from "@/lib/api/users";
import { fetchCalendars } from "@/lib/api/calendar";
import { calendarOptionLabel } from "@/lib/calendars";
import {
  createSessionBlock,
  fetchSessionBlock,
  updateSessionBlock,
} from "@/lib/api/admin-session-blocks";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";
import {
  ComboboxField,
  FieldLabel,
  TimePicker,
  dateToYmd,
  isValidHhmm,
  toDoctorOption,
  toTenantOption,
  ymdToDate,
} from "../day-view/form-fields";
import {
  PillButton,
  RecurrenceFields,
  sortMonthSentinelValues,
  validateRecurrence,
} from "../day-view/recurrence-fields";
import {
  buildDefaultIntervals,
  buildIntervals,
  cycleIntervalSubType,
  intervalCounts,
  intervalsSummary,
  offsetClock,
  slotCountForSpan,
  suggestLabelFromTimes,
} from "./sessions-utils";

const COMMON_TIMEZONES = [
  "UTC",
  "Europe/Warsaw",
  "Europe/London",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

const LABEL_PRESETS = ["AM", "PM", "EVE"];

// Timezone to prefill for a NEW record. Honors the app-wide global timezone override when set (so
// newly-created records match what the rest of the app shows), else the machine zone as before.
function browserTimezone() {
  return appTimeZone();
}

function toCalendarOption(item) {
  const value = String(item?.id || item?._id || "").trim();
  if (!value) return null;
  // Rule hints ("· non-blocking · local only") ride the label so the picker explains itself.
  const label = calendarOptionLabel(item) || value;
  return { value, label, raw: item };
}

function optionsArray(maybeArray) {
  if (!Array.isArray(maybeArray)) return [];
  return maybeArray
    .map((entry) => {
      if (!entry) return null;
      const value = String(entry.id || entry.value || "").trim();
      if (!value) return null;
      const label = String(entry.name || entry.label || value).trim();
      return { value, label, raw: entry };
    })
    .filter(Boolean);
}

const DEFAULT_FORM = {
  doctorId: "",
  tenantId: "",
  calendarId: "",
  label: "AM",
  labelTouched: false,
  startTime: "09:00",
  endTime: "11:30",
  timezone: "UTC",
  effectiveFrom: "",
  recurrence: {
    frequency: "weekly",
    monthlyVariant: "nthWeekday",
    weekdays: [],
    monthDays: [],
    monthWeeks: [],
    repeatIntervalWeeks: 1,
    repeatIntervalMonths: 1,
    dates: [],
    excludeDates: [],
  },
  // The interval template. `intervalsTouched` false ⇒ keep whatever is here
  // (migrated pattern on edit, auto layout on create) and re-default on time
  // changes; true ⇒ the user owns it (tick clicks or stepper re-layouts).
  intervals: [],
  intervalsTouched: false,
  colorHex: "",
};

function deriveInitialForm({ mode, block, defaultDoctorId, defaultTenantId, defaultWeekday, canonicalDoc }) {
  if (mode === "edit" && (canonicalDoc || block)) {
    const source = canonicalDoc || block?.raw || block || {};
    const recurrence = source.recurrence || {};
    const monthDays = Array.isArray(recurrence.monthDays) ? recurrence.monthDays : [];
    const monthWeeks = Array.isArray(recurrence.monthWeeks) ? recurrence.monthWeeks : [];
    const intervals = Array.isArray(source.intervals) ? source.intervals : [];
    return {
      ...DEFAULT_FORM,
      doctorId: String(source.doctorId || "").trim(),
      tenantId: String(source.tenantId || "").trim(),
      calendarId: String(source.calendarId || "").trim(),
      label: String(source.label || "").trim() || suggestLabelFromTimes(source.startTime),
      labelTouched: true,
      startTime: String(source.startTime || "09:00"),
      endTime: String(source.endTime || "11:30"),
      timezone: String(source.timezone || browserTimezone()),
      effectiveFrom: String(source.effectiveFrom || "").trim(),
      recurrence: {
        frequency: String(recurrence.frequency || "weekly").toLowerCase() === "monthly" ? "monthly" : "weekly",
        monthlyVariant: monthDays.length > 0 ? "dayOfMonth" : "nthWeekday",
        weekdays: Array.isArray(recurrence.weekdays) ? [...recurrence.weekdays].sort((a, b) => a - b) : [],
        monthDays: sortMonthSentinelValues(monthDays),
        monthWeeks: sortMonthSentinelValues(monthWeeks),
        repeatIntervalWeeks: Number(recurrence.repeatIntervalWeeks) || 1,
        repeatIntervalMonths: Number(recurrence.repeatIntervalMonths) || 1,
        dates: Array.isArray(recurrence.dates) ? [...recurrence.dates].sort() : [],
        excludeDates: Array.isArray(recurrence.excludeDates) ? [...recurrence.excludeDates].sort() : [],
      },
      intervals,
      intervalsTouched: false,
      colorHex: String(source.metadata?.colorHex || "").trim(),
    };
  }

  const startTime = "09:00";
  const endTime = "11:30";
  return {
    ...DEFAULT_FORM,
    doctorId: String(defaultDoctorId || "").trim(),
    tenantId: String(defaultTenantId || "").trim(),
    timezone: browserTimezone(),
    effectiveFrom: dateToYmd(new Date()),
    recurrence: {
      ...DEFAULT_FORM.recurrence,
      weekdays: Number.isInteger(defaultWeekday) ? [defaultWeekday] : [],
    },
    intervals: buildDefaultIntervals(startTime, endTime),
  };
}

const SUB_TYPE_TICK = {
  firstVisit: { label: "First visit", short: "N", className: "bg-sky-500/85 text-white hover:bg-sky-400" },
  followUp: { label: "Follow-up", short: "F", className: "bg-violet-500/85 text-white hover:bg-violet-400" },
  "": { label: "Open (any)", short: "·", className: "bg-muted-foreground/25 text-foreground/70 hover:bg-muted-foreground/40" },
};

// Clickable tick strip of the template, seeded from the current slot
// distribution. Each tick is one bookable slot; clicking it cycles
// First visit → Follow-up → Open. Identity keys and extra metadata survive
// the click, so saving updates the existing child slots in place.
function IntervalPatternEditor({ intervals, startTime, onCycle }) {
  if (!Array.isArray(intervals) || intervals.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {intervals.map((interval, idx) => {
        const subType = String(interval?.subType || "");
        const tick = SUB_TYPE_TICK[subType] || SUB_TYPE_TICK[""];
        const clock = offsetClock(startTime, interval.offsetMin);
        return (
          <button
            key={interval.key || idx}
            type="button"
            onClick={() => onCycle(idx)}
            title={`${clock} · ${interval.durationMin} min · ${tick.label} — click to change`}
            aria-label={`${clock}, ${interval.durationMin} minutes, ${tick.label}. Click to change.`}
            className={cn(
              "grid h-6 place-items-center rounded-[4px] text-[9px] font-bold transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              tick.className,
            )}
            style={{ width: Math.max(18, Math.round((interval.durationMin || 15) * 1.4)) }}
          >
            {tick.short}
          </button>
        );
      })}
    </div>
  );
}

function PatternLegend() {
  return (
    <div className="flex flex-wrap items-center gap-3 text-[10px] text-muted-foreground">
      {Object.entries(SUB_TYPE_TICK).map(([key, tick]) => (
        <span key={key || "open"} className="inline-flex items-center gap-1">
          <span className={cn("grid size-3.5 place-items-center rounded-[3px] text-[8px] font-bold", tick.className)}>
            {tick.short}
          </span>
          {tick.label}
        </span>
      ))}
    </div>
  );
}

export function SessionBlockFormSheet({
  open,
  onOpenChange,
  mode = "create",
  block = null,
  defaultDoctorId = "",
  defaultTenantId = "",
  defaultWeekday = null,
  doctorOptions: doctorOptionsProp,
  tenantOptions: tenantOptionsProp,
  onSaved,
  onDelete,
}) {
  const [form, setForm] = useState(() =>
    deriveInitialForm({ mode, block, defaultDoctorId, defaultTenantId, defaultWeekday }),
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const [tenantOpts, setTenantOpts] = useState(() => optionsArray(tenantOptionsProp));
  const [doctorOpts, setDoctorOpts] = useState(() => optionsArray(doctorOptionsProp));
  const [calendarOpts, setCalendarOpts] = useState([]);
  const [calendarsLoading, setCalendarsLoading] = useState(false);
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const calendarsRequestRef = useRef(0);
  const canonicalRequestRef = useRef(0);

  // Reset + hydrate on every (re)open. Edit mode refetches the canonical doc
  // so intervals/overrides are fresh even when the row came from a stale list.
  useEffect(() => {
    if (!open) return;
    setForm(deriveInitialForm({ mode, block, defaultDoctorId, defaultTenantId, defaultWeekday }));
    setErrors({});
    setSubmitError("");

    if (mode !== "edit" || !block?.id) return;
    const requestId = canonicalRequestRef.current + 1;
    canonicalRequestRef.current = requestId;
    setCanonicalLoading(true);
    fetchSessionBlock({ blockId: block.id })
      .then((doc) => {
        if (canonicalRequestRef.current !== requestId) return;
        if (!doc || typeof doc !== "object") return;
        setForm(
          deriveInitialForm({ mode, block, defaultDoctorId, defaultTenantId, defaultWeekday, canonicalDoc: doc }),
        );
      })
      .catch(() => {
        // Keep the row-derived form.
      })
      .finally(() => {
        if (canonicalRequestRef.current === requestId) setCanonicalLoading(false);
      });
  }, [open, mode, block, defaultDoctorId, defaultTenantId, defaultWeekday]);

  useEffect(() => {
    if (!open) return;
    if (Array.isArray(tenantOptionsProp) && tenantOptionsProp.length > 0) {
      setTenantOpts(optionsArray(tenantOptionsProp));
      return;
    }
    fetchTenants()
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setTenantOpts(items.map(toTenantOption).filter(Boolean));
      })
      .catch(() => setTenantOpts([]));
  }, [open, tenantOptionsProp]);

  useEffect(() => {
    if (!open) return;
    if (Array.isArray(doctorOptionsProp) && doctorOptionsProp.length > 0) {
      setDoctorOpts(optionsArray(doctorOptionsProp));
      return;
    }
    fetchDoctors()
      .then((res) => {
        const items = Array.isArray(res?.items) ? res.items : [];
        setDoctorOpts(items.map(toDoctorOption).filter(Boolean));
      })
      .catch(() => setDoctorOpts([]));
  }, [open, doctorOptionsProp]);

  // Calendar list follows the chosen doctor (same as the slot editor).
  useEffect(() => {
    if (!open) return;
    if (!form.doctorId) {
      setCalendarOpts([]);
      return;
    }
    const requestId = calendarsRequestRef.current + 1;
    calendarsRequestRef.current = requestId;
    setCalendarsLoading(true);
    fetchCalendars({ doctorId: form.doctorId })
      .then((res) => {
        if (calendarsRequestRef.current !== requestId) return;
        const items = Array.isArray(res?.items) ? res.items : [];
        const opts = items.map(toCalendarOption).filter(Boolean);
        setCalendarOpts(opts);
        setForm((prev) => {
          if (prev.calendarId && opts.some((opt) => opt.value === prev.calendarId)) return prev;
          // Auto-pick the doctor's only calendar (the common case here).
          return { ...prev, calendarId: opts.length === 1 ? opts[0].value : prev.calendarId && opts.some((o) => o.value === prev.calendarId) ? prev.calendarId : opts.length > 0 ? opts[0].value : "" };
        });
      })
      .catch(() => {
        if (calendarsRequestRef.current !== requestId) return;
        setCalendarOpts([]);
      })
      .finally(() => {
        if (calendarsRequestRef.current === requestId) setCalendarsLoading(false);
      });
  }, [open, form.doctorId]);

  // Timezone prefill from the selected calendar.
  useEffect(() => {
    if (!form.calendarId) return;
    const selectedCalendar = calendarOpts.find((opt) => opt.value === form.calendarId);
    const tz = String(selectedCalendar?.raw?.timezone || "").trim();
    if (tz) {
      setForm((prev) => (prev.timezone === tz ? prev : { ...prev, timezone: tz }));
    }
  }, [form.calendarId, calendarOpts]);

  const totalSlots = slotCountForSpan(form.startTime, form.endTime);
  // Live counts derived from the current distribution — the single source the
  // tick strip, the steppers, and the summary all read.
  const counts = intervalCounts(form.intervals);

  // Time-span changes re-default the pattern while the user hasn't touched it,
  // and re-suggest the label while it hasn't been pinned.
  const applyTimeChange = (key) => (value) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (!prev.labelTouched) next.label = suggestLabelFromTimes(next.startTime);
      if (isValidHhmm(next.startTime) && isValidHhmm(next.endTime) && !prev.intervalsTouched) {
        next.intervals = buildDefaultIntervals(next.startTime, next.endTime);
      }
      return next;
    });
  };

  // Stepper edit: regenerate an evenly-spread layout with the requested mix;
  // slots beyond firstVisit+followUp stay Open.
  const applyCounts = (fv, fu) => {
    setForm((prev) => ({
      ...prev,
      intervalsTouched: true,
      intervals: buildIntervals(prev.startTime, prev.endTime, Math.max(0, fv), Math.max(0, fu)),
    }));
  };

  // Tick click: cycle just that slot's type, keeping its identity key and any
  // extra metadata — the base is always the current distribution.
  const cycleInterval = (index) => {
    setForm((prev) => ({
      ...prev,
      intervalsTouched: true,
      intervals: prev.intervals.map((interval, i) => (i === index ? cycleIntervalSubType(interval) : interval)),
    }));
  };

  const validate = () => {
    const next = {};
    if (!form.doctorId) next.doctorId = "Doctor is required.";
    if (!form.tenantId) next.tenantId = "Clinic is required.";
    if (!form.calendarId) next.calendarId = "Calendar is required.";
    if (!isValidHhmm(form.startTime)) next.startTime = "Set a start time.";
    if (!isValidHhmm(form.endTime)) next.endTime = "Set an end time.";
    if (isValidHhmm(form.startTime) && isValidHhmm(form.endTime) && form.endTime <= form.startTime) {
      next.endTime = "End must be after start.";
    }
    if (!form.timezone.trim()) next.timezone = "Timezone is required.";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom)) next.effectiveFrom = "Pick the first active date.";
    Object.assign(next, validateRecurrence(form.recurrence, { allowDatesFrequency: false }));
    if (totalSlots <= 0) next.endTime = next.endTime || "The block is too short for any bookable slot.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const buildPayload = () => {
    const rec = form.recurrence;
    const recurrence = { frequency: rec.frequency };
    if (rec.frequency === "weekly") {
      recurrence.weekdays = [...rec.weekdays].sort((a, b) => a - b);
      recurrence.repeatIntervalWeeks = Math.max(1, Number(rec.repeatIntervalWeeks) || 1);
    } else {
      recurrence.repeatIntervalMonths = Math.max(1, Number(rec.repeatIntervalMonths) || 1);
      if (rec.monthlyVariant === "dayOfMonth") {
        recurrence.monthDays = sortMonthSentinelValues(rec.monthDays);
      } else {
        recurrence.monthWeeks = sortMonthSentinelValues(rec.monthWeeks);
        recurrence.weekdays = [...rec.weekdays].sort((a, b) => a - b);
      }
    }
    if (rec.dates.length > 0) recurrence.dates = [...rec.dates].sort();
    if (rec.excludeDates.length > 0) recurrence.excludeDates = [...rec.excludeDates].sort();

    // Untouched intervals on edit keep their keys (identity-preserving); a
    // stepper-regenerated layout sends keyless intervals and lets the server
    // assign fresh ones.
    const intervals = form.intervals.map((interval) => {
      const item = {
        offsetMin: interval.offsetMin,
        durationMin: interval.durationMin,
      };
      if (interval.key) item.key = interval.key;
      if (interval.subType) item.subType = interval.subType;
      if (interval.bookingMode) item.bookingMode = interval.bookingMode;
      if (interval.placeholder) item.placeholder = interval.placeholder;
      if (interval.metadata) item.metadata = interval.metadata;
      return item;
    });

    return {
      tenantId: form.tenantId,
      doctorId: form.doctorId,
      calendarId: form.calendarId,
      label: form.label.trim(),
      startTime: form.startTime,
      endTime: form.endTime,
      timezone: form.timezone.trim(),
      effectiveFrom: form.effectiveFrom,
      recurrence,
      intervals,
      metadata: form.colorHex ? { colorHex: form.colorHex } : {},
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError("");
    try {
      const body = buildPayload();
      let response;
      if (mode === "edit" && block?.id) {
        // doctorId is immutable server-side; drop it from the PUT.
        const { doctorId: _doctorId, ...updateBody } = body;
        response = await updateSessionBlock({ blockId: block.id, body: updateBody });
      } else {
        response = await createSessionBlock({ body });
      }
      const reconcile = response?.reconcile;
      const warnings = Array.isArray(response?.warnings) ? response.warnings : [];
      toast.success(mode === "edit" ? "Session block updated" : "Session block created", {
        description: reconcile
          ? `Bookable slots: ${reconcile.created} added, ${reconcile.updated} updated, ${reconcile.deleted} removed.`
          : undefined,
      });
      for (const warning of warnings) {
        toast.warning(warning);
      }
      publishCalendarRefresh({ source: "session-block-form" });
      onSaved?.();
      onOpenChange?.(false);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Failed to save the session block.");
    } finally {
      setSubmitting(false);
    }
  };

  const effectiveFromDate = form.effectiveFrom ? ymdToDate(form.effectiveFrom) : null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
        <SheetHeader className="border-b border-border px-6 py-4">
          <SheetTitle className="flex items-center gap-2">
            {mode === "edit" ? "Edit session block" : "New session block"}
            {canonicalLoading ? (
              <Loader2 className="size-4 animate-spin text-muted-foreground" aria-hidden />
            ) : null}
          </SheetTitle>
          <SheetDescription>
            {mode === "edit"
              ? "Edits the whole recurring session — its bookable slots are regenerated to match. Use the Week view for one-day exceptions."
              : "A session block is a doctor's working session at one clinic (e.g. Monday AM). It generates the bookable slots automatically."}
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-full min-h-0 flex-1">
          <form onSubmit={handleSubmit} className="space-y-5 px-6 py-5 pb-32">
            <ComboboxField
              label="Doctor"
              required
              value={form.doctorId}
              options={doctorOpts}
              disabled={mode === "edit"}
              onChange={(value) => setForm((prev) => ({ ...prev, doctorId: value }))}
              error={errors.doctorId}
              placeholder="Choose doctor"
            />
            <ComboboxField
              label="Clinic"
              required
              value={form.tenantId}
              options={tenantOpts}
              onChange={(value) => setForm((prev) => ({ ...prev, tenantId: value }))}
              error={errors.tenantId}
              placeholder="Choose clinic"
            />
            <ComboboxField
              label="Calendar"
              required
              value={form.calendarId}
              options={calendarOpts}
              loading={calendarsLoading}
              disabled={!form.doctorId}
              onChange={(value) => setForm((prev) => ({ ...prev, calendarId: value }))}
              error={errors.calendarId}
              placeholder={form.doctorId ? "Choose calendar" : "Pick a doctor first"}
            />

            <Separator />

            <div className="space-y-2">
              <FieldLabel>Session label</FieldLabel>
              <div className="flex flex-wrap items-center gap-2">
                {LABEL_PRESETS.map((preset) => (
                  <PillButton
                    key={preset}
                    active={form.label === preset}
                    onClick={() => setForm((prev) => ({ ...prev, label: preset, labelTouched: true }))}
                  >
                    {preset}
                  </PillButton>
                ))}
                <Input
                  value={LABEL_PRESETS.includes(form.label) ? "" : form.label}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, label: e.target.value, labelTouched: true }))
                  }
                  placeholder="Custom…"
                  className="h-8 w-28"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <FieldLabel>Start</FieldLabel>
                <TimePicker
                  value={form.startTime}
                  onChange={applyTimeChange("startTime")}
                  error={Boolean(errors.startTime)}
                />
                {errors.startTime ? <p className="text-xs text-destructive">{errors.startTime}</p> : null}
              </div>
              <div className="space-y-2">
                <FieldLabel>End</FieldLabel>
                <TimePicker
                  value={form.endTime}
                  onChange={applyTimeChange("endTime")}
                  error={Boolean(errors.endTime)}
                />
                {errors.endTime ? <p className="text-xs text-destructive">{errors.endTime}</p> : null}
              </div>
            </div>

            <div className="space-y-2">
              <FieldLabel>Timezone</FieldLabel>
              <Input
                list="session-block-tz-list"
                value={form.timezone}
                onChange={(e) => setForm((prev) => ({ ...prev, timezone: e.target.value.trim() }))}
                placeholder="Asia/Singapore"
                className="h-9"
              />
              <datalist id="session-block-tz-list">
                {COMMON_TIMEZONES.map((tz) => (
                  <option key={tz} value={tz} />
                ))}
              </datalist>
              {errors.timezone ? <p className="text-xs text-destructive">{errors.timezone}</p> : null}
            </div>

            <div className="space-y-2">
              <FieldLabel>Active from</FieldLabel>
              <p className="text-xs text-muted-foreground/80">
                The first date the session can occur. Also anchors "every N weeks" cadences.
              </p>
              <DatePicker
                date={effectiveFromDate}
                setDate={(next) => setForm((prev) => ({ ...prev, effectiveFrom: dateToYmd(next) }))}
              />
              {errors.effectiveFrom ? <p className="text-xs text-destructive">{errors.effectiveFrom}</p> : null}
            </div>

            <Separator />

            <RecurrenceFields
              value={form.recurrence}
              onChange={(patch) =>
                setForm((prev) => ({ ...prev, recurrence: { ...prev.recurrence, ...patch } }))
              }
              errors={errors}
              allowDayOfMonth
              allowDatesFrequency={false}
            />

            <Separator />

            <div className="space-y-2.5 rounded-lg border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <FieldLabel>Bookable slot pattern</FieldLabel>
                <span className="text-xs font-medium text-foreground/80">{intervalsSummary(form.intervals)}</span>
              </div>
              <IntervalPatternEditor
                intervals={form.intervals}
                startTime={form.startTime}
                onCycle={cycleInterval}
              />
              <PatternLegend />
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div className="space-y-1">
                  <span className="text-[11px] text-muted-foreground">First visit</span>
                  <Input
                    type="number"
                    min={0}
                    max={totalSlots}
                    value={counts.firstVisit}
                    onChange={(e) => applyCounts(Number(e.target.value) || 0, counts.followUp)}
                    className="h-9"
                  />
                </div>
                <div className="space-y-1">
                  <span className="text-[11px] text-muted-foreground">Follow-up</span>
                  <Input
                    type="number"
                    min={0}
                    max={totalSlots}
                    value={counts.followUp}
                    onChange={(e) => applyCounts(counts.firstVisit, Number(e.target.value) || 0)}
                    className="h-9"
                  />
                </div>
              </div>
              <p className="text-[11px] leading-relaxed text-muted-foreground">
                Click a slot in the strip to set what it takes — the base is the current
                distribution, so a migrated block's hand-tuned order is preserved until you change
                it. The steppers re-lay the whole pattern out evenly; slots beyond the two counts
                stay Open.
              </p>
            </div>

            <div className="space-y-2">
              <FieldLabel>Color</FieldLabel>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.colorHex || "#4c9aff"}
                  onChange={(e) => setForm((prev) => ({ ...prev, colorHex: e.target.value }))}
                  className="h-9 w-12 cursor-pointer rounded-md border border-border bg-background p-1"
                  aria-label="Session color"
                />
                <Input
                  value={form.colorHex}
                  onChange={(e) => setForm((prev) => ({ ...prev, colorHex: e.target.value.trim() }))}
                  placeholder="#rrggbb (optional)"
                  className="h-9 w-32 font-mono"
                />
                {form.colorHex ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8"
                    onClick={() => setForm((prev) => ({ ...prev, colorHex: "" }))}
                  >
                    Clear
                  </Button>
                ) : null}
              </div>
            </div>

            {submitError ? (
              <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                {submitError}
              </p>
            ) : null}
          </form>
        </ScrollArea>

        <div className="flex items-center justify-between gap-2 border-t border-border bg-background px-6 py-4">
          {mode === "edit" && onDelete && block ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-destructive hover:bg-destructive/10 hover:text-destructive [&_svg]:size-3.5"
              onClick={() => onDelete(block)}
            >
              <Trash2 />
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" size="sm" disabled={submitting} onClick={() => onOpenChange?.(false)}>
              Cancel
            </Button>
            <Button type="button" size="sm" disabled={submitting} onClick={handleSubmit}>
              {submitting ? <Spinner className="size-3.5" /> : null}
              {mode === "edit" ? "Save changes" : "Create block"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
